import React from "react";
import {
  BlueBirdTheme, Button, Divider, Form, Loading, Typography,
} from "@kitbird/core";
import styled from "styled-components";
import Table from "../../../../../components/Table";
import useApprovalVendor from "../../hooks/useApprovalVendor";
import { ReactComponent as ShowIcon } from "../../../../../assets/icons/show_icon.svg";
import { ReactComponent as PdfIcon } from "../../../../../assets/icons/pdf_file.svg";
import { ReactComponent as ImgIcon } from "../../../../../assets/icons/img_icon.svg";
import Modal from "../../../../../components/Modal";
import { ModalType } from "../../constants";
import { FormGroup, FormWrapper } from "../../../../../components/Form/Form.style";
import LabelAndValue from "../../../../Transaction/CreateReceiptSubmission/components/LabelAndValue";
import { FileItem, FileWrapper } from "../../../../Transaction/CreateReceiptSubmission/components/styles";
import { getDocUrl } from "../../../../../services/bbOneGcsService";
import { byteStringToFileSize, toStringDate } from "../../../../../utils/common";
import ChecklistLine, { ChecklistDataI } from "../../../../../components/ChecklistLine";
import { listCodeTypeAll } from "../../../../Transaction/CreateReceiptSubmission/constants";

interface ModalApprovalI {
  useCustomHook: ReturnType<typeof useApprovalVendor>;
}

const {
  black, danger, gray, primary, secondary,
} = BlueBirdTheme.colors;

const Segment = styled.div`
  display: grid;
  gap: 16px;
  padding: 16px;
  `;

const BgOverflow = styled.div`
  background-color: ${gray.background};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const ModalApproval = ({ useCustomHook }: ModalApprovalI) => {
  const {
    detail,
    isOpen,
    setOpen,
    rejectReason,
    setRejectReason,
    isLoading,
    isLoadingDetail,
    handleApprove,
  } = useCustomHook;

  const isReject = isOpen === ModalType.REJECT;
  const isApprove = isOpen === ModalType.APPROVE;
  const isDetail = isOpen === ModalType.DETAIL;
  const title = `
    ${isReject ? "Penolakan " : ""}
    ${isApprove ? "Persetujuan " : ""}
    ${isDetail ? "Detail " : ""}
    master vendor
  `;

  const generateChecklist: () => ChecklistDataI[] = () => {
    const rows = detail ? [...detail.approvals] : [];

    const approvalLine: any[] = rows.reverse().map((approval, i, arr) => {
      const isActive = approval.status_approval !== "WAITING" && approval.status_approval !== "OPEN";
      const isCreated = approval.status_approval === "CREATED";
      const isApproved = approval.status_approval === "APPROVED" && approval.job_flow === "APPROVER";
      const isRejected = approval.status_approval === "REJECTED"
        && (approval.job_flow === "APPROVER" || approval.job_flow === "EXECUTE");
      const isExecuted = approval.status_approval === "APPROVED" && approval.job_flow === "EXECUTE";

      const jobFlowLabel = (val: number) => ({
        CREATOR: "Pemohon",
        APPROVER: `Approval level ${val}`,
        EXECUTE: "Transfer dana",
      })[approval.job_flow];

      const approvalDescription = ([
        { desc: "Dibuat : ", condition: isCreated },
        { desc: "Disetujui : ", condition: isApproved },
        { desc: "Ditolak : ", condition: isRejected },
        { desc: "Diproses : ", condition: isExecuted },
      ]).find((item) => item.condition)?.desc;

      return (
        {
          key: i,
          label: (
            <Typography
              level={6}
              fontWeight={isActive ? "medium" : "regular"}
              color={isActive ? black : gray.disable}
            >
              {jobFlowLabel(arr.length - i - 1)}
            </Typography>
          ),
          description: (<div>
            <Typography
              level={6}
              color={isActive ? black : gray.disable}
            >
              {approval.employee_name}
            </Typography>
            <br />
            {isActive
              && <Typography
                level={7}
                color={isRejected ? danger[500] : primary[500]}
                fontWeight="medium"
              >
                {approvalDescription}
                {toStringDate(approval.approval_end, true)}
              </Typography>
            }
          </div>),
          active: isActive,
          checkType: isRejected ? "cross" : "check",
        }
      );
    });

    return approvalLine;
  };

  const onClose = () => {
    setOpen(undefined);
    setRejectReason("");
  };

  return (
    <Modal
      title={title}
      isOpen={!!isOpen}
      setOpen={() => onClose()}
      width={isDetail ? 750 : undefined}
      footer={<>
        {(isApprove || isReject)
          && <Button
            data-testid="modal-confirm-cancel"
            type="button"
            variant="text"
            onClick={() => onClose()}
            disabled={isLoading}
          >
            <Typography level={5} color={primary[500]} fontWeight="bold" >
              Batal
            </Typography>
          </Button>
        }
        {isApprove
          && <Button
            data-testid="modal-confirm-submit"
            type="submit"
            backgroundColor={primary[400]}
            onClick={handleApprove}
          >
            <Typography level={5} color="white" fontWeight="bold">
              Setujui
            </Typography>
          </Button>
        }
        {isReject
          && <Button
            data-testid="modal-confirm-submit"
            type="submit"
            backgroundColor={rejectReason && danger[400]}
            onClick={handleApprove}
            disabled={!rejectReason}
          >
            <Typography level={5} color="white" fontWeight="bold">
              Tolak
            </Typography>
          </Button>
        }
      </>}
      body={<>
        {isLoadingDetail && <Loading variant="circular" />}
        {(detail && isReject)
          && <FormGroup>
            <FormWrapper>
              <Form label="Alasan tolak" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
            </FormWrapper>
          </FormGroup>
        }
        {(detail && (isApprove || isDetail))
          && <>
            <div style={{
              margin: "-16px",
              overflowY: "auto",
              maxHeight: "70vh",
              height: "",

              display: "grid",
              gridTemplateColumns: isDetail ? "250px auto" : "auto",
            }}>
              {(() => {
                if (isDetail) {
                  return <BgOverflow>
                    <Typography level={6} fontWeight="medium">Status</Typography>
                    <ChecklistLine data={generateChecklist()} />
                  </BgOverflow>;
                } return undefined;
              })()}
              <div>
                <Segment>
                  <Typography level={6} fontWeight="medium">Detail vendor</Typography>
                  <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", gap: 16 }}>
                    <LabelAndValue label="Kode vendor" value={detail.vendor_code} />
                    <LabelAndValue label="Nama vendor" value={detail.vendor_name} />
                    <LabelAndValue label="Alamat vendor" value={detail.vendor_address} />
                    <LabelAndValue label="Tipe vendor" value={detail.type_vendor} />
                  </div>
                </Segment>
                <Divider color={secondary[200]} />
                <Segment>
                  <Typography level={6} fontWeight="medium">Detail rekening vendor</Typography>
                  <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", gap: 16 }}>
                    <LabelAndValue label="Bank" value={detail.bank_account} />
                    <LabelAndValue label="No. rekening" value={detail.bank_account_number} />
                    <LabelAndValue label="Nama pemilik rekening" value={detail.bank_account_name} />
                  </div>
                </Segment>
                <Divider color={secondary[200]} />
                <Segment>
                  <Typography level={6} fontWeight="medium">Area terkait</Typography>
                  <Table columns={[
                    {
                      title: "Code type",
                      dataIndex: "code_type",
                      key: "code_type",
                      render: (item) => listCodeTypeAll.find(
                        (codeType) => codeType.code_type === item,
                      )?.code_type_desc,
                    },
                    {
                      title: "Area",
                      dataIndex: "area_desc",
                      key: "area_desc",
                      render: (item) => item.replace(/[^a-zA-Z\s]/g, ""),
                    },
                    {
                      title: "Pool",
                      dataIndex: "pool_id",
                      key: "pool_id",
                      render: (item, row) => `${row.pool_name} • ${item}`,
                    },
                    {
                      title: "Departemen",
                      dataIndex: "department",
                      key: "department",
                    },
                  ]} dataSource={detail.pool_department} />
                </Segment>
                <Divider color={secondary[200]} />
                <div style={{ display: "grid", gap: 16, padding: 16 }}>
                  <Typography level={6} fontWeight="medium">Lampiran</Typography>
                  <FileWrapper>
                    {detail.vendor_files.map((doc) => (
                      <FileItem onClick={async () => window.open(await getDocUrl(doc))}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {doc.file_name.split(".").pop() === "pdf" ? <PdfIcon /> : <ImgIcon />}
                        </div>
                        <div className="ellipsis-text">
                          {doc.file_name}
                          <div>
                            <Typography level={7} fontWeight="medium">{
                              byteStringToFileSize(doc.file_size)}</Typography>
                          </div>
                        </div>
                        <div style={{ height: 21.6, display: "flex", alignItems: "center" }}>
                          <ShowIcon style={{ color: gray.disable }} />
                        </div>
                      </FileItem>
                    ))}
                  </FileWrapper>
                </div>
              </div>
            </div>
          </>
        }
      </>}
    />
  );
};

export default ModalApproval;
